/**
 * GoogleReviews Component
 * =======================
 * Displays Google reviews with rating summary and individual review cards.
 * Supports both curated reviews and Elfsight widget integration.
 */

import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  REVIEWS_CONFIG,
  GOOGLE_RATING,
  CURATED_REVIEWS
} from '../config/reviews';
import Reveal from './ui/Reveal';
import usePrefersReducedMotion from '../hooks/usePrefersReducedMotion';

const StarRating = ({ rating, size = 'md' }) => {
  const sizeClasses = { sm: 'w-3.5 h-3.5', md: 'w-4 h-4', lg: 'w-5 h-5' };
  return (
    <div className="flex items-center gap-0.5" aria-label={`${rating} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((star) => (
        <svg key={star} className={`${sizeClasses[size]} ${star <= rating ? 'text-amber-bright' : 'text-line'}`} fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
    </div>
  );
};

const GoogleLogo = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
);

const ReviewCard = ({ review }) => {
  const [expanded, setExpanded] = useState(false);
  const shouldTruncate = review.text.length > 200;
  const displayText = shouldTruncate && !expanded ? review.text.slice(0, 200) + '...' : review.text;

  return (
    <article className="border border-line bg-card p-6 transition-[border-color,box-shadow] duration-300 ease-out hover:border-amber-500/40 hover:shadow-soft">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h4 className="font-display text-lg text-cream">{review.author}</h4>
          <p className="text-xs font-sans text-cream-faint">{review.date}</p>
        </div>
        <GoogleLogo />
      </div>
      <div className="mb-3"><StarRating rating={review.rating} size="sm" /></div>
      <p className="font-sans text-cream-dim text-sm leading-relaxed">{displayText}</p>
      {shouldTruncate && (
        <button onClick={() => setExpanded(!expanded)} className="mt-3 py-2 font-sans text-amber-bright text-xs font-semibold link-underline">
          {expanded ? 'Show less' : 'Read more'}
        </button>
      )}
    </article>
  );
};

const RatingSummary = () => {
  const reduced = usePrefersReducedMotion();
  // Detected on this card (always a real, non-zero size) rather than on
  // each bar itself: a bar starts at scaleX:0 (zero visual width), and an
  // element with zero area can fail to ever register as "intersecting"
  // with its own whileInView observer, leaving it permanently invisible.
  // Driving every bar's animate prop from one parent-level detection
  // sidesteps that entirely.
  const [barsVisible, setBarsVisible] = useState(false);

  return (
    <motion.div
      className="border border-line bg-card p-6"
      onViewportEnter={() => setBarsVisible(true)}
      viewport={{ once: true, margin: '-40px' }}
    >
      <div className="flex items-center gap-2 mb-5">
        <GoogleLogo />
        <span className="font-sans text-xs uppercase tracking-widest2 text-cream-faint">Google Reviews</span>
      </div>

      <div className="flex items-center gap-4 mb-5">
        <span className="font-display text-5xl text-cream">{GOOGLE_RATING.overallRating}</span>
        <div>
          <StarRating rating={Math.round(GOOGLE_RATING.overallRating)} size="lg" />
          <p className="font-sans text-xs text-cream-faint mt-1">Based on {GOOGLE_RATING.totalReviews} reviews</p>
        </div>
      </div>

      <div className="space-y-1.5">
        {[5, 4, 3, 2, 1].map((stars, i) => (
          <div key={stars} className="flex items-center gap-2">
            <span className="font-sans text-xs text-cream-faint w-2.5">{stars}</span>
            <div className="flex-1 h-1 bg-line overflow-hidden">
              {reduced ? (
                <div className="h-full bg-amber-bright" style={{ width: `${GOOGLE_RATING.breakdown[stars]}%` }} />
              ) : (
                <motion.div
                  className="h-full bg-amber-bright origin-left"
                  style={{ width: `${GOOGLE_RATING.breakdown[stars]}%` }}
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: barsVisible ? 1 : 0 }}
                  transition={{ duration: 0.7, delay: i * 0.06, ease: [0.16, 1, 0.3, 1] }}
                />
              )}
            </div>
            <span className="font-sans text-xs text-cream-faint w-7 text-right">{GOOGLE_RATING.breakdown[stars]}%</span>
          </div>
        ))}
      </div>

      <a
        href={REVIEWS_CONFIG.GOOGLE_MAPS_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-6 w-full inline-flex items-center justify-center gap-2 py-3 border border-amber-500/50 text-amber-bright font-sans text-sm font-semibold hover:bg-amber-500 hover:text-ink transition-colors"
      >
        Write a Review
      </a>
    </motion.div>
  );
};

// Elfsight widget — dark styling applies unconditionally since the site
// no longer has a light/dark toggle (it was previously scoped to a
// `.dark` ancestor class that this redesign removed).
const ElfsightWidget = () => {
  useEffect(() => {
    if (!document.getElementById('elfsight-script')) {
      const script = document.createElement('script');
      script.id = 'elfsight-script';
      script.src = 'https://elfsightcdn.com/platform.js';
      script.async = true;
      document.body.appendChild(script);
    }

    if (!document.getElementById('elfsight-dark-mode-fix')) {
      const style = document.createElement('style');
      style.id = 'elfsight-dark-mode-fix';
      style.textContent = `
        .elfsight-app-${REVIEWS_CONFIG.ELFSIGHT_WIDGET_ID},
        .elfsight-app-${REVIEWS_CONFIG.ELFSIGHT_WIDGET_ID} * {
          color: #1f2937 !important;
        }
        .elfsight-app-${REVIEWS_CONFIG.ELFSIGHT_WIDGET_ID} button,
        .elfsight-app-${REVIEWS_CONFIG.ELFSIGHT_WIDGET_ID} [class*="button"],
        .elfsight-app-${REVIEWS_CONFIG.ELFSIGHT_WIDGET_ID} [class*="btn"],
        .elfsight-app-${REVIEWS_CONFIG.ELFSIGHT_WIDGET_ID} [class*="load-more"],
        .elfsight-app-${REVIEWS_CONFIG.ELFSIGHT_WIDGET_ID} [class*="write-review"],
        .elfsight-app-${REVIEWS_CONFIG.ELFSIGHT_WIDGET_ID} [class*="cta"] {
          color: #1C1512 !important;
          background-color: #E8792D !important;
          border-color: #E8792D !important;
          border-radius: 0 !important;
        }
        .elfsight-app-${REVIEWS_CONFIG.ELFSIGHT_WIDGET_ID} button:hover,
        .elfsight-app-${REVIEWS_CONFIG.ELFSIGHT_WIDGET_ID} [class*="button"]:hover,
        .elfsight-app-${REVIEWS_CONFIG.ELFSIGHT_WIDGET_ID} [class*="btn"]:hover {
          background-color: #FF9A4D !important;
        }
        .elfsight-app-${REVIEWS_CONFIG.ELFSIGHT_WIDGET_ID} a[href*="elfsight"],
        .elfsight-app-${REVIEWS_CONFIG.ELFSIGHT_WIDGET_ID} [class*="eapps-link"],
        .elfsight-app-${REVIEWS_CONFIG.ELFSIGHT_WIDGET_ID} [class*="powered"],
        .elfsight-app-${REVIEWS_CONFIG.ELFSIGHT_WIDGET_ID} [class*="branding"],
        .elfsight-app-${REVIEWS_CONFIG.ELFSIGHT_WIDGET_ID} [class*="free-widget"],
        a[href*="elfsight.com"][class*="eapps"],
        [class*="eapps"][class*="link"] {
          display: none !important;
          visibility: hidden !important;
          opacity: 0 !important;
          height: 0 !important;
          overflow: hidden !important;
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  return <div className={`elfsight-app-${REVIEWS_CONFIG.ELFSIGHT_WIDGET_ID}`} data-elfsight-app-lazy />;
};

const SectionHeader = () => (
  <Reveal className="text-center max-w-xl mx-auto mb-12">
    <p className="eyebrow mb-4">What our customers say</p>
    <h2 className="text-4xl lg:text-5xl text-cream mb-4">Loved by our community</h2>
    <p className="font-sans text-cream-dim">Don't just take our word for it.</p>
  </Reveal>
);

const GoogleReviews = () => {
  if (REVIEWS_CONFIG.USE_ELFSIGHT && REVIEWS_CONFIG.ELFSIGHT_WIDGET_ID) {
    return (
      <section className="py-20 lg:py-28 bg-surface">
        <div className="container-custom">
          <SectionHeader />
          <ElfsightWidget />
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 lg:py-28 bg-surface">
      <div className="container-custom">
        <SectionHeader />

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <Reveal delay={0.05} className="lg:col-span-1 self-start">
            <RatingSummary />
          </Reveal>

          <div className="lg:col-span-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {CURATED_REVIEWS.slice(0, 4).map((review, i) => (
                <Reveal key={review.id} delay={0.1 + i * 0.05}>
                  <ReviewCard review={review} />
                </Reveal>
              ))}
            </div>

            <Reveal delay={0.3} className="text-center mt-8">
              <a
                href={REVIEWS_CONFIG.GOOGLE_MAPS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 font-sans text-cream-dim hover:text-amber-bright font-semibold link-underline transition-colors"
              >
                View all {GOOGLE_RATING.totalReviews} reviews on Google
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
              </a>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GoogleReviews;
